import {extractUniqueFormulaVarNames, resolveVariableReferenceValue} from './formulaUtils';
import {mergeProductVars, reconcileProductVars} from './services/products/productService';

const dedupeNames = (names = []) => Array.from(new Set(names.filter(Boolean)));

export const collectUniqueFormulaVars = (texts = []) =>
    dedupeNames(texts.flatMap((text) => extractUniqueFormulaVarNames(text)));

export const buildScopedProductGroups = ({
    products = [],
    baseVars = [],
    managedProductVarNames = [],
    uniqueVarNames = [],
    preferredProductId = '',
}) => {
    if (!products.length) return [];

    const normalizedUniqueVarNames = dedupeNames(uniqueVarNames);
    if (!normalizedUniqueVarNames.length) {
        return [
            {
                groupKey: 'shared',
                uniqueVarNames: [],
                products: [...products],
                templateProduct:
                    products.find((product) => product.id === preferredProductId) || products[0],
            },
        ];
    }

    const groupsByKey = new Map();

    products.forEach((product) => {
        const mergedVars = managedProductVarNames.length
            ? reconcileProductVars(baseVars, product?.vars || [], managedProductVarNames)
            : mergeProductVars(baseVars, product?.vars || []);
        const groupKey = normalizedUniqueVarNames
            .map((name) => `${name}:${resolveVariableReferenceValue(name, mergedVars).value || ''}`)
            .join('|');

        if (!groupsByKey.has(groupKey)) {
            groupsByKey.set(groupKey, {
                groupKey,
                uniqueVarNames: normalizedUniqueVarNames,
                products: [],
            });
        }

        groupsByKey.get(groupKey).products.push(product);
    });

    return Array.from(groupsByKey.values()).map((group) => ({
        ...group,
        templateProduct:
            group.products.find((product) => product.id === preferredProductId) || group.products[0],
    }));
};

export const buildScopedGenerationTasks = ({
    products = [],
    baseVars = [],
    managedProductVarNames = [],
    preferredProductId = '',
    topic = '',
    descriptionContext = '',
    rows = [],
    enabled = {},
    titleUniqueVarNames = null,
    descriptionUniqueVarNames = null,
    bulletUniqueVarNamesByIndex = null,
}) => {
    const normalizedTitleUniqueVarNames = enabled.title
        ? dedupeNames(
              titleUniqueVarNames ?? collectUniqueFormulaVars([topic]),
          )
        : [];
    const normalizedDescriptionUniqueVarNames = enabled.desc
        ? dedupeNames(
              descriptionUniqueVarNames ??
                  collectUniqueFormulaVars([topic, descriptionContext]),
          )
        : [];
    const rowsEnabled = enabled.bullets || enabled.faqs;
    const rowTaskType = enabled.faqs ? 'faq' : 'bullet';
    const normalizedRowUniqueVarNamesByIndex = rowsEnabled
        ? rows.map((row, rowIndex) =>
              dedupeNames(
                  bulletUniqueVarNamesByIndex?.[rowIndex] ??
                      collectUniqueFormulaVars([topic, row?.field || '', row?.kw || '']),
              ),
          )
        : [];
    const hasScopedUniqueGeneration =
        products.length > 1 &&
        (normalizedTitleUniqueVarNames.length > 0 ||
            normalizedDescriptionUniqueVarNames.length > 0 ||
            normalizedRowUniqueVarNamesByIndex.some((names) => names.length > 0));

    if (!hasScopedUniqueGeneration) {
        return [
            {
                type: 'full',
                productIds: products.map((product) => product.id),
                templateProductId:
                    products.find((product) => product.id === preferredProductId)?.id ||
                    products[0]?.id ||
                    '',
                uniqueVarNames: [],
            },
        ];
    }

    const tasks = [];

    if (enabled.title) {
        buildScopedProductGroups({
            products,
            baseVars,
            managedProductVarNames,
            uniqueVarNames: normalizedTitleUniqueVarNames,
            preferredProductId,
        }).forEach((group) => {
            tasks.push({
                type: 'title',
                productIds: group.products.map((product) => product.id),
                templateProductId: group.templateProduct?.id || '',
                uniqueVarNames: normalizedTitleUniqueVarNames,
            });
        });
    }

    if (enabled.desc) {
        buildScopedProductGroups({
            products,
            baseVars,
            managedProductVarNames,
            uniqueVarNames: normalizedDescriptionUniqueVarNames,
            preferredProductId,
        }).forEach((group) => {
            tasks.push({
                type: 'description',
                productIds: group.products.map((product) => product.id),
                templateProductId: group.templateProduct?.id || '',
                uniqueVarNames: normalizedDescriptionUniqueVarNames,
            });
        });
    }

    if (rowsEnabled) {
        rows.forEach((row, rowIndex) => {
            buildScopedProductGroups({
                products,
                baseVars,
                managedProductVarNames,
                uniqueVarNames: normalizedRowUniqueVarNamesByIndex[rowIndex] || [],
                preferredProductId,
            }).forEach((group) => {
                tasks.push({
                    type: rowTaskType,
                    rowIndex,
                    rowId: row?.id || '',
                    productIds: group.products.map((product) => product.id),
                    templateProductId: group.templateProduct?.id || '',
                    uniqueVarNames: normalizedRowUniqueVarNamesByIndex[rowIndex] || [],
                });
            });
        });
    }

    return tasks;
};
